import { createSelector } from "@reduxjs/toolkit";

import { selectMultiImages } from "../../../../components/ImageUploader/images.slice";
import { FormId } from "../../../../components/enums";
import { RootState } from "../../../../store";
import { UIState } from "../../../../store/HOC/genericUI.slice";
import {
  filterEmpty,
  generateAddress,
  generatePhone,
  mapDocumentTypeToLabel,
} from "../../../../utilities/forms";
import { parseDataToDDMMYYYY } from "../../../../utilities/functions";

import { ComponentId as CircumstancesComponentId } from "./CircumstancesData/enums";
import { ComponentId as DriverComponentId } from "./DriverData/enums";
import { ComponentId as OtherPersonsComponentId } from "./OtherPersonsData/enums";
import { ComponentId as StatementsComponentId } from "./StatementsData/enums";
import { ComponentId as WitnessesComponentId } from "./WitnessesData/enums";
import {
  IncidentCircumstancesFormInputs,
  IncidentDriverFormInputs,
  IncidentOtherPersonsFormInputs,
  IncidentStatementsFormInputs,
  IncidentWitnessesFormInputs,
} from "./incident.slice";

export const selectIncidentCircumstancesData = (
  store: RootState
): IncidentCircumstancesFormInputs =>
  store.ocVehicle.incident.data[FormId.INCIDENT_CIRCUMSTANCES];

export const selectIncidentDriverData = (
  store: RootState
): IncidentDriverFormInputs =>
  store.ocVehicle.incident.data[FormId.INCIDENT_DRIVER];

export const selectIncidentStatementsData = (
  store: RootState
): IncidentStatementsFormInputs =>
  store.ocVehicle.incident.data[FormId.INCIDENT_STATEMENTS];

export const selectIncidentWitnessesData = (
  store: RootState
): IncidentWitnessesFormInputs =>
  store.ocVehicle.incident.data[FormId.INCIDENT_WITNESSES];

export const selectIncidentOtherPersonsData = (
  store: RootState
): IncidentOtherPersonsFormInputs =>
  store.ocVehicle.incident.data[FormId.INCIDENT_OTHER_PERSONS];

export const selectIncidentOtherPersonsParticipantsData = (
  store: RootState
): Partial<IncidentOtherPersonsFormInputs> => ({
  [OtherPersonsComponentId.PERSONS]:
    store.ocVehicle.incident.data[FormId.INCIDENT_OTHER_PERSONS][
      OtherPersonsComponentId.PERSONS
    ],
});

export const selectIncidentWitnessesPersonsData = (
  store: RootState
): Partial<IncidentWitnessesFormInputs> => ({
  [WitnessesComponentId.PERSONS]:
    store.ocVehicle.incident.data[FormId.INCIDENT_WITNESSES][
      WitnessesComponentId.PERSONS
    ],
});

export const selectWasVehicleInMotion = createSelector(
  selectIncidentCircumstancesData,
  (circumstancesData) =>
    circumstancesData[
      CircumstancesComponentId.CIRCUMSTANCES_WAS_VEHICLE_IN_MOTION
    ] === "true"
);

export const selectWasVehicleNotInMotion = createSelector(
  selectIncidentCircumstancesData,
  (circumstancesData) =>
    circumstancesData[
      CircumstancesComponentId.CIRCUMSTANCES_WAS_VEHICLE_IN_MOTION
    ] === "false"
);

export const selectIsPeselUndefined = createSelector(
  selectIncidentDriverData,
  (driverData) => driverData[DriverComponentId.DRIVER_LACK_OF_PESEL] === "true"
);

export const selectIsPoliceStatementSelected = createSelector(
  selectIncidentStatementsData,
  (
    statementsData: Pick<
      IncidentStatementsFormInputs,
      StatementsComponentId.INCIDENT_STATEMENTS_TYPE
    >
  ) =>
    statementsData[StatementsComponentId.INCIDENT_STATEMENTS_TYPE] === "police"
);

export const selectIsPerpetratorStatementSelected = createSelector(
  selectIncidentStatementsData,
  (
    statementsData: Pick<
      IncidentStatementsFormInputs,
      StatementsComponentId.INCIDENT_STATEMENTS_TYPE
    >
  ) =>
    statementsData[StatementsComponentId.INCIDENT_STATEMENTS_TYPE] ===
    "perpetrator"
);

export const selectAreInjuredPersons = createSelector(
  selectIncidentOtherPersonsData,
  (otherPersonsData) =>
    otherPersonsData[OtherPersonsComponentId.ARE_INJURED_PERSONS] === "true"
);

export const selectGotInjuredPersonsDetails = createSelector(
  selectIncidentOtherPersonsData,
  (otherPersonsData) =>
    otherPersonsData[OtherPersonsComponentId.GOT_INJURED_PERSONS_DETAILS] ===
    "true"
);

export const selectIsWitnessesDataAvailable = createSelector(
  selectIncidentWitnessesData,
  (witnessesData) =>
    witnessesData[WitnessesComponentId.IS_WITNESSES_DATA_AVAILABLE] === "true"
);

const selectApplicantDriver = createSelector(
  selectIncidentCircumstancesData,
  (circumstancesData) =>
    circumstancesData[CircumstancesComponentId.CIRCUMSTANCES_WAS_DRIVER]
);

const selectApplicantZipCode = createSelector(
  selectIncidentCircumstancesData,
  (circumstancesData) =>
    circumstancesData[CircumstancesComponentId.CIRCUMSTANCES_ZIP_CODE_PL]
);

const selectIncidentDriver = createSelector(
  selectApplicantDriver,
  (applicantDriver) => ({
    name: "Czy Pan/Pani kierował/a pojazdem w trakcie zdarzenia?",
    value: applicantDriver && (applicantDriver === "true" ? "tak" : "nie"),
  })
);

const selectIncidentDateTime = createSelector(
  selectIncidentCircumstancesData,
  (data) => ({
    name: "Data i godzina",
    value: [
      parseDataToDDMMYYYY(data[CircumstancesComponentId.CIRCUMSTANCES_DATE]),
      data[CircumstancesComponentId.CIRCUMSTANCES_HOUR],
    ].join(", "),
  })
);

const selectIncidentPlace = createSelector(
  selectIncidentCircumstancesData,
  (data) => ({
    name: "Miejsce",
    value: data[CircumstancesComponentId.CIRCUMSTANCES_PLACE],
  })
);

const selectIncidentAddress = createSelector(
  selectIncidentCircumstancesData,
  (data) => ({
    name: "Miejscowość, województwo, kraj",
    value: generateAddress(data),
  })
);

const selectIncidentWasVehicleInMotion = createSelector(
  selectWasVehicleInMotion,
  (wasInMotion) => ({
    name: "Czy pojazd był w ruchu?",
    value: wasInMotion ? "tak" : "nie",
  })
);

const selectIncidentCircumstancesDescription = createSelector(
  selectIncidentCircumstancesData,
  (data) => ({
    name: "Opis zdarzenia",
    value: data[CircumstancesComponentId.CIRCUMSTANCES_DESCRIPTION],
  })
);

const selectPoliceDepartment = createSelector(
  selectIncidentStatementsData,
  (data) => ({
    name: "Jednostka policji",
    value: data[StatementsComponentId.INCIDENT_STATEMENTS_POLICE_DEPARTMENT],
  })
);

const selectCaseNumber = createSelector(
  selectIncidentStatementsData,
  (data) => ({
    name: "Numer sprawy",
    value: data[StatementsComponentId.INCIDENT_STATEMENTS_CASE_NUMBER],
  })
);

const selectPoliceImages = createSelector(
  (data: RootState) =>
    selectMultiImages(data, {
      section: StatementsComponentId.INCIDENT_STATEMENTS_POLICE_IMAGES,
    }),
  (data) => ({
    name: "Protokół policji",
    value: Object.values(data).map((image) => image.src as string),
  })
);

const selectIncidentPoliceStatements = createSelector(
  selectPoliceDepartment,
  selectCaseNumber,
  selectPoliceImages,
  (policeDepartment, caseNumber, images) => [
    policeDepartment,
    caseNumber,
    images,
  ]
);

const selectPerpetratorStatementsImages = createSelector(
  (data: RootState) =>
    selectMultiImages(data, {
      section: StatementsComponentId.INCIDENT_STATEMENTS_PERPETRATOR_IMAGES,
    }),
  (data) => {
    return [
      {
        name: "Oświadczenia sprawcy",
        value: Object.values(data).map((image) => image.src as string),
      },
    ];
  }
);

const selectIncidentStatements = createSelector(
  selectIsPoliceStatementSelected,
  (state: RootState) => state,
  (isPoliceStatement, state) => [
    {
      name: "Typ dokumentu",
      value: isPoliceStatement ? "protokół policji" : "oświadczenie sprawcy",
    },
    ...(isPoliceStatement
      ? selectIncidentPoliceStatements(state)
      : selectPerpetratorStatementsImages(state)),
  ]
);

const selectIncidentCircumstancesSummaryData = createSelector(
  selectIncidentDriver,
  selectIncidentDateTime,
  selectIncidentPlace,
  selectIncidentAddress,
  selectIncidentWasVehicleInMotion,
  selectIncidentCircumstancesDescription,
  selectIncidentStatements,
  (
    driver,
    dateTime,
    place,
    address,
    wasVehicleInMotion,
    description,
    statements
  ) => [
    driver,
    dateTime,
    place,
    address,
    wasVehicleInMotion,
    description,
    ...statements,
  ]
);

const selectDriverNameSurname = createSelector(
  selectIncidentDriverData,
  (data) => ({
    name: "Imię i nazwisko",
    value: [
      data[DriverComponentId.DRIVER_NAME],
      data[DriverComponentId.DRIVER_SURNAME],
    ]
      .join(" ")
      .trim(),
  })
);

const selectDriverTel = createSelector(selectIncidentDriverData, (data) => ({
  name: "Tel.",
  value: generatePhone(data[DriverComponentId.DRIVER_PHONE]),
}));

const selectDriverAddress = createSelector(
  selectIncidentDriverData,
  (data) => ({
    name: "Ulica, kod pocztowy, miejscowość",
    value: generateAddress(data),
  })
);

const selectDriverPesel = createSelector(selectIncidentDriverData, (data) => ({
  name: "PESEL",
  value: data[DriverComponentId.DRIVER_PESEL],
}));

const selectDriverOtherIdDetails = createSelector(
  selectIncidentDriverData,
  (data) => ({
    name: "Rodzaj i numer dokumentu",
    value: [
      mapDocumentTypeToLabel[data[DriverComponentId.DRIVER_OTHER_ID]],
      data[DriverComponentId.DRIVER_OTHER_ID_NUMBER],
    ]
      .filter(filterEmpty)
      .join(" - "),
  })
);

const selectDriverId = createSelector(
  selectIsPeselUndefined,
  (state: RootState) => state,
  (isPeselUndefined, state) =>
    isPeselUndefined
      ? selectDriverOtherIdDetails(state)
      : selectDriverPesel(state)
);

const selectIncidentDriverDetails = createSelector(
  selectDriverNameSurname,
  selectDriverTel,
  selectDriverId,
  selectDriverAddress,
  (nameSurname, tel, id, address) =>
    [nameSurname, tel, id, address].filter((item) => item.value !== "")
);

const selectIncidentDriverSummaryData = createSelector(
  selectWasVehicleInMotion,
  (state: RootState) => state,
  (wasInMotion, state) =>
    wasInMotion ? selectIncidentDriverDetails(state) : []
);

const selectIncidentWitnessesPersons = createSelector(
  selectIncidentWitnessesPersonsData,
  (data) =>
    data.persons !== undefined
      ? Object.values(data.persons).map((person, index) => ({
          name: `Osoba ${++index}`,
          value: [
            [person.name, person.surname].join(" ").trim(),
            generatePhone(person.phone),
            person.email,
            [
              [person.street, person.street2].join(" "),
              [person.zipCode, person.city].join(" "),
            ]
              .filter((item) => item.trim() !== "")
              .join(", "),
          ]
            .filter((item) => item.trim() !== "")
            .join("\r\n"),
        }))
      : []
);

const selectIncidentWitnessesSummaryData = createSelector(
  selectIsWitnessesDataAvailable,
  (state) => state,
  (shouldDisplay, state) =>
    !shouldDisplay ? [] : selectIncidentWitnessesPersons(state)
);

const selectNumberOfParticipants = createSelector(
  selectIncidentOtherPersonsData,
  (data) => ({
    name: "Liczba osób w chwili zdarzenia",
    value: data[OtherPersonsComponentId.NUMBER_OF_PARTICIPANTS],
  })
);

const selectInjuredPersonsData = createSelector(
  selectIncidentOtherPersonsData,
  (data) =>
    data.persons !== undefined
      ? Object.values(data.persons).map((person, index) => ({
          name: `Osoba ${++index}`,
          value: [
            [person.name, person.surname].join(" ").trim(),
            generatePhone(person.phone),
            person.email,
            [
              [person.street, person.street2].join(" "),
              [person.zipCode, person.city].join(" "),
            ]
              .filter((item) => item.trim() !== "")
              .join(", "),
          ]
            .filter((item) => item.trim() !== "")
            .join("\r\n"),
        }))
      : []
);

const selectInjuredPersonsDetails = createSelector(
  selectGotInjuredPersonsDetails,
  (state: RootState) => state,
  (gotInjuredPersons, state) => [
    {
      name: "Czy posiadasz dane poszkodowanych osób?",
      value: gotInjuredPersons ? "tak" : "nie",
    },
    ...(gotInjuredPersons ? selectInjuredPersonsData(state) : []),
  ]
);

const selectInjuredPersons = createSelector(
  selectAreInjuredPersons,
  selectInjuredPersonsDetails,
  (areInjuredPersons, injuredPersonsDetails) => [
    {
      name: "Czy są osoby poszkodowane?",
      value: areInjuredPersons ? "tak" : "nie",
    },
    ...injuredPersonsDetails,
  ]
);

const selectIncidentOtherPersonsSummaryData = createSelector(
  selectNumberOfParticipants,
  selectInjuredPersons,
  (numberOfParticipants, injuredPersons) => [
    numberOfParticipants,
    ...injuredPersons,
  ]
);

export const selectSummaryData = createSelector(
  selectIncidentDriverSummaryData,
  selectIncidentCircumstancesSummaryData,
  selectIncidentOtherPersonsSummaryData,
  selectIncidentWitnessesSummaryData,
  (driver, circumstances, otherPersons, witnesses) => ({
    driver,
    circumstances,
    otherPersons,
    witnesses,
  })
);

export const selectFormData = createSelector(
  selectIncidentCircumstancesData,
  selectIncidentDriverData,
  selectIncidentStatementsData,
  selectIncidentOtherPersonsData,
  selectIncidentWitnessesData,
  (
    circumstances,
    driver,
    statements,
    { persons: otherPersons, ...restOtherPersons },
    { persons, ...restWitnesses }
  ) => ({
    circumstances,
    driver,
    statements,
    otherPersons: {
      [OtherPersonsComponentId.PERSONS]: Object.values(otherPersons),
      ...restOtherPersons,
    },
    witnesses: {
      [WitnessesComponentId.PERSONS]: Object.values(persons),
      ...restWitnesses,
    },
  })
);

export const selectUIData = (store: RootState): UIState =>
  store.ocVehicle.incident.ui;

export const selectCircumAvailableCities = (store: RootState) =>
  store.ocVehicle.incident.ui.circumAvailableCities || [];
